<template>
  <div class="blog-post">
    <section class="hero-image">
      <img src="/images/blog/reef-maintenance-hero.jpg" alt="Happy reef keeper cleaning glass on aquarium" />
    </section>
    <section class="blog-meta">
      <span class="author">By <strong>Bill Pankau</strong></span>
      <span class="date">| Sept 12, 2025</span>
    </section>
    <h1>Reef Tank Maintenance Schedule Template (Free Download)</h1>
    <section class="intro">
      <p>
        A healthy reef tank isn’t just about water chemistry—it’s about consistent maintenance. From cleaning glass to testing parameters, every task helps keep your system stable and your corals thriving.
      </p>
      <p>
        To make life easier, we’ve created a free reef tank maintenance schedule template you can download and use today. Even better? Tools like <a href="https://reeflendar.com" target="_blank" rel="noopener">Reeflendar.com</a> can automate these reminders and track your progress for you.
      </p>
      <figure class="blog-image">
        <img src="/images/blog/cleaning-tank-glass.jpg" alt="Happy reef keeper cleaning glass on aquarium" />
        <figcaption>Happy reef keeper cleaning glass on aquarium</figcaption>
      </figure>
    </section>

    <section>
      <h2>Why a Maintenance Schedule Matters</h2>
      <p>
        Reef tanks are delicate ecosystems. Skipping a water test, delaying a filter change, or forgetting to dose supplements can cause instability that stresses corals and fish. A written or digital maintenance schedule:
      </p>
      <ul class="benefits-list">
        <li>Keeps you consistent.</li>
        <li>Prevents tasks from piling up.</li>
        <li>Helps spot patterns when problems arise.</li>
        <li>Ensures nothing is forgotten during busy weeks.</li>
      </ul>
    </section>

    <section>
      <h2>Reef Tank Maintenance Schedule (Example Breakdown)</h2>
      <div class="schedule-section">
        <h3>🔹 Daily</h3>
        <ul>
          <li>Check temperature and salinity.</li>
          <li>Observe fish and corals for signs of stress.</li>
          <li>Top off evaporated water (if no ATO).</li>
        </ul>
        <figure class="blog-image">
          <img src="/images/blog/refractometer.jpg" alt="Reef keeper testing salinity with refractometer" />
          <figcaption>Reef keeper testing salinity with refractometer</figcaption>
        </figure>
      </div>
      <div class="schedule-section">
        <h3>🔹 Weekly</h3>
        <ul>
          <li>Test alkalinity, calcium, magnesium, nitrate, phosphate.</li>
          <li>Clean glass and remove algae.</li>
          <li>Perform 10–15% water change.</li>
          <li>Empty and clean protein skimmer cup.</li>
        </ul>
        <figure class="blog-image">
          <img src="/images/blog/water-change.jpg" alt="Performing a reef tank water change with buckets" />
          <figcaption>Performing a reef tank water change with buckets</figcaption>
        </figure>
      </div>
      <div class="schedule-section">
        <h3>🔹 Monthly</h3>
        <ul>
          <li>Deep clean pumps and powerheads.</li>
          <li>Replace carbon or other media.</li>
          <li>Calibrate testing equipment and refractometer.</li>
        </ul>
      </div>
      <div class="schedule-section">
        <h3>🔹 Quarterly</h3>
        <ul>
          <li>Replace RO/DI filters as needed.</li>
          <li>Inspect plumbing, hoses, and connections.</li>
          <li>Deep clean sump and refugium areas.</li>
        </ul>
        <figure class="blog-image">
          <img src="/images/blog/reef-sump-cleaning.jpg" alt="Reef sump being cleaned" />
          <figcaption>Reef sump being cleaned</figcaption>
        </figure>
      </div>
    </section>

    <section>
      <h2>Free Download: Reef Tank Maintenance Template</h2>
      <p>
        To help you stay organized, we’ve created a free reef tank maintenance schedule template you can download, print, and customize for your setup.
      </p>
      <div class="download-btn">
        📥 <a href="/downloads/reef-tank-maintenance-template.pdf" target="_blank" rel="noopener">Free Reef Tank Maintenance Template (PDF/Excel)</a>
      </div>
    </section>

    <section>
      <h2>The Smarter Way: Reeflendar.com</h2>
      <p>
        While printed templates are a great starting point, they rely on memory and manual tracking. That’s where <a href="https://reeflendar.com" target="_blank" rel="noopener">Reeflendar.com</a> comes in.
      </p>
      <ul class="features-list">
        <li>Get automated reminders for maintenance tasks.</li>
        <li>Log water parameters alongside your maintenance history.</li>
        <li>Attach photos to track coral growth over time.</li>
        <li>Access your schedule anytime on desktop or mobile.</li>
      </ul>
      <figure class="blog-image">
        <img src="/images/blog/reeflendar-dashboard.jpg" alt="Reeflendar dashboard screenshot with maintenance reminders" />
        <figcaption>Reeflendar dashboard screenshot with maintenance reminders</figcaption>
      </figure>
    </section>

    <section class="conclusion">
      <h2>Final Thoughts</h2>
      <p>
        Consistency is the key to a thriving reef tank. Whether you use a printed template or automate with Reeflendar, having a clear reef tank maintenance schedule keeps your aquarium stable and stress-free.
      </p>
      <p>
        👉 Download the free template now and start logging smarter with <a href="https://reeflendar.com" target="_blank" rel="noopener">Reeflendar.com</a>.
      </p>
      <figure class="blog-image">
        <img src="/images/blog/thriving-mixed-reef.jpg" alt="Thriving mixed reef aquarium with vibrant corals" />
        <figcaption>Thriving mixed reef aquarium with vibrant corals</figcaption>
      </figure>
    </section>
  </div>
</template>

<script>
import { useHead } from '@vueuse/head';

export default {
    setup() {
        useHead({
            title: "Reef Tank Maintenance Schedule Template (Free Download)",
            meta: [
                { name: 'description', content: "Keep your reef tank healthy with a consistent maintenance schedule. Download our free reef tank maintenance template and see how Reeflendar.com makes upkeep simple." },
                { name: 'keywords', content: 'reef tank maintenance schedule, aquarium maintenance template, reef aquarium checklist, reef tank upkeep guide, reef tank maintenance plan, aquarium water change schedule, reef tank maintenance download, reef keeping checklist, free reef tank maintenance schedule, reef aquarium care routine' },
            ],
        });
    },
}
</script>

<style scoped>
.blog-post {
  max-width: 700px;
  margin: 2rem auto;
  padding: 2rem;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.06);
  font-family: 'Segoe UI', Arial, sans-serif;
  line-height: 1.7;
}
.hero-image {
  width: 100%;
  margin: 0 0 1.5em 0;
  text-align: center;
}
.hero-image img {
  width: 100%;
  max-height: 320px;
  object-fit: cover;
  border-radius: 12px 12px 0 0;
  box-shadow: 0 2px 16px rgba(0,0,0,0.08);
}
.blog-meta {
  display: flex;
  align-items: center;
  font-size: 1rem;
  color: #6b7b8c;
  margin-bottom: 1.2em;
  gap: 0.5em;
}
.author strong {
  color: #1a3d4f;
}
.date {
  margin-left: 0.5em;
}
h1, h2, h3 {
  color: #1a3d4f;
  margin-top: 1.5em;
  margin-bottom: 0.5em;
}
h1 {
  font-size: 2.2rem;
}
h2 {
  font-size: 1.4rem;
}
h3 {
  font-size: 1.1rem;
  margin-top: 1.2em;
}
.blog-image {
  margin: 2em 0;
  text-align: center;
}
.blog-image img {
  max-width: 100%;
  border-radius: 8px;
  box-shadow: 0 1px 8px rgba(0,0,0,0.07);
}
.blog-image figcaption {
  font-size: 0.95em;
  color: #6b7b8c;
  margin-top: 0.5em;
}
.benefits-list, .features-list {
  margin: 1em 0 1em 1.5em;
  padding-left: 0;
}
.benefits-list li, .features-list li {
  margin-bottom: 1em;
}
.schedule-section {
  margin-bottom: 2em;
}
.download-btn {
  background: #eaf6fb;
  color: #2176ae;
  border-left: 4px solid #4fa3d1;
  padding: 0.7em 1em;
  margin: 1.5em 0;
  border-radius: 6px;
  font-size: 1.1em;
  font-weight: 500;
}
a {
  color: #2b8ec6;
  text-decoration: underline;
}
a:hover {
  color: #1a3d4f;
}
.conclusion {
  margin-top: 2em;
  border-top: 1px solid #e0e7ef;
  padding-top: 1.5em;
}
</style>
